import { useState } from 'react';
import type { CharacterSummary } from '@/domain/types';
import { MAX_CHARACTER_SLOTS } from '@/state/profileSync';
import './CharacterSelectScreen.css';

const CLASS_LABEL: Record<string, string> = {
  hollow_knight: 'Hollow-Knight',
};

interface Props {
  /** All characters belonging to the user (or to local-mode storage). */
  characters: CharacterSummary[];
  /** Continue with an existing character. */
  onSelect: (characterId: string) => void;
  /** Open the create-new-character flow for the given empty slot. */
  onCreate: (slotIndex: number) => void;
  /** Permanently delete a character + its progress. Caller handles confirm-
   *  dialog routing; this fires only after confirmation. */
  onDelete: (characterId: string) => void;
  /** Footer link — back to login (signed-in user) or back to start screen
   *  (local mode). The wording is supplied by the caller because the
   *  flow target differs. */
  footerLinkLabel: string;
  onFooterLink: () => void;
}

function formatLastPlayed(ts: number): string {
  if (!ts) return 'noch nie';
  const minutes = (Date.now() - ts) / 60000;
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${Math.floor(minutes)} Min`;
  const hours = minutes / 60;
  if (hours < 24) return `vor ${Math.floor(hours)} Std`;
  const days = hours / 24;
  if (days < 30) return `vor ${Math.floor(days)} Tg`;
  return `vor ${Math.floor(days / 30)} Mon`;
}

export function CharacterSelectScreen({
  characters, onSelect, onCreate, onDelete, footerLinkLabel, onFooterLink,
}: Props) {
  // Slot index → character (or undefined if empty). Caps at MAX_CHARACTER_SLOTS
  // so the layout stays predictable even if the server ever returns a stray
  // out-of-range index.
  const bySlot: (CharacterSummary | undefined)[] = Array.from({ length: MAX_CHARACTER_SLOTS });
  for (const c of characters) {
    if (c.characterIndex >= 0 && c.characterIndex < MAX_CHARACTER_SLOTS) {
      bySlot[c.characterIndex] = c;
    }
  }

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  return (
    <div className="char-select">
      <div className="char-select__bg" />
      <div className="char-select__content">
        <h1 className="char-select__title">CHARAKTER WÄHLEN</h1>
        <p className="char-select__tagline">3 Slots · Hollow-Knight Klasse</p>

        <div className="char-select__slots">
          {bySlot.map((c, idx) => {
            if (!c) {
              return (
                <button
                  key={`empty-${idx}`}
                  type="button"
                  className="char-slot char-slot--empty"
                  onClick={() => onCreate(idx)}
                >
                  <span className="char-slot__index">+</span>
                  <span>Slot {idx + 1} — Neuer Charakter</span>
                </button>
              );
            }
            const isPending = pendingDelete === c.id;
            return (
              <div
                key={c.id}
                className="char-slot char-slot--filled"
                role="button"
                tabIndex={0}
                onClick={() => { if (!isPending) onSelect(c.id); }}
                onKeyDown={(e) => {
                  if (!isPending && (e.key === 'Enter' || e.key === ' ')) onSelect(c.id);
                }}
              >
                <span className="char-slot__index">{idx + 1}</span>
                <div className="char-slot__body">
                  <span className="char-slot__name">{c.name}</span>
                  <span className="char-slot__meta">
                    Lvl {c.level} ·{' '}
                    <span className="char-slot__class">
                      {CLASS_LABEL[c.characterClass] ?? c.characterClass}
                    </span>{' '}
                    · {formatLastPlayed(c.lastTickAt)}
                  </span>
                </div>
                {isPending ? (
                  <button
                    type="button"
                    className="char-slot__delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(c.id);
                      setPendingDelete(null);
                    }}
                  >
                    Wirklich löschen?
                  </button>
                ) : (
                  <button
                    type="button"
                    className="char-slot__delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(c.id);
                    }}
                    aria-label="Charakter löschen"
                  >
                    Löschen
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="char-select__footer">
          <button type="button" className="char-select__link" onClick={onFooterLink}>
            {footerLinkLabel}
          </button>
          <span>{characters.length} / {MAX_CHARACTER_SLOTS} Slots</span>
        </div>
      </div>
    </div>
  );
}
